import { useSectionVisible } from 'appContainer/SectionVisible';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { CLAIM_RETURN_ACTIONS } from 'types/Claim';
import { NOT_SUBMITTED_STATUSES } from 'types/Claims';
import { SECTIONS } from 'types/SectionsVisibility';

export const useClaimHook = () => {
	const { claimData, showClaimComment } = useSelector(
		(state: RootState) => state.claim,
	);
	const { complaintDetail } = useSelector(
		(state: RootState) => state.complaintTracking,
	);
	const { fraudDetail } = useSelector((state: RootState) => state.fraudTracking);
	const { claimReturnStatuses } = useSelector((state: RootState) => state.form);
	const [isReviewReady, setIsReviewReady] = useState(false);

	useEffect(() => {
		if (!claimData) {
			setIsReviewReady(false);
			return;
		}

		const reviewFieldsAreComplete = Object.values(claimData.receipt).every(
			field => !!field,
		);

		const attachmentsAreComplete =
			!!claimData.attachments.prescription && !!claimData.attachments.receipt;

		setIsReviewReady(reviewFieldsAreComplete && attachmentsAreComplete);
	}, [claimData]);

	// only if the claim is not already in review and
	// only if the claim is rejected and
	// based on carrier config
	const claimReviewEnabled = useSectionVisible(SECTIONS.ENABLE_CLAIM_REVIEW, {
		customLayout: claimData.customLayout,
	});
	const enableClaimReview =
		claimData.status.id === 8 && !claimData.reviewClaim && claimReviewEnabled;

	// only if the claim is in the Paid Voided status and
	// based on carrier config
	const claimRekeyEnabled = useSectionVisible(SECTIONS.ENABLE_CLAIM_REKEY, {
		customLayout: claimData.customLayout,
	});
	const enableClaimRekey = claimData.status.id === 13 && claimRekeyEnabled;

	const showTaxColumns = useSectionVisible(SECTIONS.PAYMENT_TAX_FIELDS, {
		customLayout: claimData.customLayout || null,
	});

	const claimApproveButtonEnabled = useSectionVisible(
		SECTIONS.ENABLE_CLAIM_APPROVE,
		{
			customLayout: claimData.customLayout,
		},
	);
	const enableClaimApprove =
		claimApproveButtonEnabled && claimData.status.id === 3;

	const claimRejectButtonEnabled = useSectionVisible(
		SECTIONS.ENABLE_CLAIM_REJECT,
		{
			customLayout: claimData.customLayout,
		},
	);
	const enableClaimReject =
		claimRejectButtonEnabled && claimData.status.id === 3;

	const sectionPending = useSectionVisible(SECTIONS.ENABLE_CLAIM_PENDING, {
		customLayout: claimData.customLayout,
	});
	const enableClaimPending = claimData.status.id === 3 && sectionPending;

	const enableClaimVoid = claimData.status.id === 5;

	const showInternalNote =
		claimData.status.id === 8 &&
		claimData.status.internalNote &&
		!claimData.status.reasonCode;

	const showPendingNote =
		[3, 18].includes(claimData.status.id) && claimData.status.internalNote;

	const showRejectedNote =
		[8].includes(claimData.status.id) &&
		claimData.status.internalNote &&
		claimData.status.reasonCode;

	const complaintEnabledForCarrier = useSectionVisible(
		SECTIONS.ENABLE_COMPLAINT,
		{
			customLayout: claimData.customLayout,
		},
	);

	const enableClaimComplaint =
		!complaintDetail.complaintId && complaintEnabledForCarrier;

	const fraudEnabledForCarrier = useSectionVisible(SECTIONS.ENABLE_FRAUD, {
		customLayout: claimData.customLayout,
	});
	const enableClaimFraud = !fraudDetail.fraudId && fraudEnabledForCarrier;

	const showReturnStatus = !!(
		claimData.returnDetail?.status &&
		claimReturnStatuses.map(e => e.code).includes(claimData.returnDetail?.status)
	);
	const enableClaimReturn =
		!NOT_SUBMITTED_STATUSES.includes(claimData.status.id) &&
		showReturnStatus &&
		!Object.values(CLAIM_RETURN_ACTIONS).includes(
			claimData.returnDetail?.status as CLAIM_RETURN_ACTIONS,
		);

	const enableExtendedRejectionReasonCodes = useSectionVisible(
		SECTIONS.REASON_CODES_REJECTED_EXTENDED,
		{
			customLayout: claimData.customLayout,
		},
	);

	const hasComments = !!claimData.comments;
	const showClaimCommentsButton = !hasComments && !showClaimComment;
	const showClaimCommentsForm = showClaimComment;
	const showClaimCommentsView = hasComments && !showClaimComment;

	return {
		isReviewReady,
		enableClaimReview,
		enableClaimRekey,
		showTaxColumns,
		enableClaimApprove,
		enableClaimReject,
		enableClaimPending,
		enableClaimVoid,
		showInternalNote,
		showPendingNote,
		enableClaimComplaint,
		enableClaimFraud,
		showReturnStatus,
		enableClaimReturn,
		showRejectedNote,
		enableExtendedRejectionReasonCodes,
		showClaimCommentsButton,
		showClaimCommentsForm,
		showClaimCommentsView,
	};
};
